import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { get } from "helpers/api_helper"

const listLabels = { mainList: "Main List", waitingList: "Waiting List", preRegistrationList: "Sorting List" }
const playerName = player => [player.firstName, player.lastName].filter(Boolean).join(" ") || (player.userId ? `Player #${player.userId}` : "Unidentified player")
const checkErrors = {
  INVALID_COMPETITION_LEVEL_LOCKED: () => "A draw or match has now been recorded, which locks the level. Reload the current details.",
  INVALID_COMPETITION_LEVEL: check => `Level ${check.level} is no longer offered. Reload the current details and choose another level.`,
  COMPETITION_LEVEL_UNVERIFIED: () => "The players' current levels could not be read.",
}
// Refusals that checking the same level again cannot change.
const finalCheckErrors = ["INVALID_COMPETITION_LEVEL_LOCKED", "INVALID_COMPETITION_LEVEL"]

export const levelConflictMessage = ({ level, entries, conflicts }, savedLevel) => `${conflicts.length} of ${entries} registered ${entries === 1 ? "entry" : "entries"} ${
  conflicts.length === 1 ? "does" : "do"} not fit level ${level}. Withdraw ${conflicts.length === 1 ? "it" : "them"} in Entries or keep level ${savedLevel}.`

// The check of a new level against the competition's active entries in the Main List, the Waiting List and the Sorting List, with the
// rules of registration on each player's current level (API competitionEditor/levelChange.js). It runs when the administrator picks
// another level; the save waits for a check that accepts it, and the API checks again when it saves.
export function useLevelCheck({ editorPath, savedLevel, level, enabled }) {
  const [state, setState] = useState({ status: "idle" })
  const [attempt, setAttempt] = useState(0)
  const changed = enabled && level !== "" && level != null && String(level) !== String(savedLevel)
  useEffect(() => {
    if (!changed) { setState({ status: "idle" }); return }
    let alive = true
    setState({ status: "checking", level: String(level) })
    get(`${editorPath}/levels/${encodeURIComponent(level)}`).then(response => {
      const data = response?.data
      if (String(data?.level) !== String(level) || !Number.isInteger(data.entries) || !Array.isArray(data.conflicts)) throw new Error("Invalid level check")
      if (alive) setState({ ...data, status: data.conflicts.length ? "conflicts" : "accepted", level: String(level) })
    }).catch(error => { if (alive) setState({ status: "error", level: String(level), code: error?.response?.data?.message }) })
    return () => { alive = false }
  }, [changed, level, editorPath, attempt])
  return { ...state, changed, retry: () => setAttempt(value => value + 1) }
}

// The level field's error while a new level is not accepted yet.
export const levelCheckError = (check, savedLevel) => !check.changed ? undefined
  : check.status === "conflicts" ? levelConflictMessage(check, savedLevel)
    : check.status === "accepted" ? undefined
      : check.status === "error" && check.code === "INVALID_COMPETITION_LEVEL" ? "Choose another level."
        : check.status === "error" ? "The registered players could not be checked against the new level. Check again before saving."
          : "Wait until the registered players are checked against the new level."

export default function CompetitionLevelCheck({ check, savedLevel, entriesLink }) {
  if (!check.changed) return null
  if (check.status === "checking") return <p role="status" className="text-muted">Checking the registered players against level {check.level}…</p>
  if (check.status === "error") return <div role="alert" className="alert alert-warning">
    {Object.prototype.hasOwnProperty.call(checkErrors, check.code) ? checkErrors[check.code](check) : `The registered players could not be checked against level ${check.level}.`}{" "}
    {!finalCheckErrors.includes(check.code) && <button type="button" className="btn btn-link p-0 align-baseline" onClick={check.retry}>Check again</button>}
  </div>
  if (check.status === "accepted") return check.entries > 0 ? <p role="status" className="alert alert-success">
    {check.entries === 1 ? "The registered entry fits" : `All ${check.entries} registered entries fit`} level {check.level}. Players are not notified of the change automatically; use Announcements to tell them.
  </p> : null
  if (check.status !== "conflicts") return null
  return <div role="alert" className="alert alert-danger competition-level-conflicts">
    <p>{levelConflictMessage(check, savedLevel)}</p>
    <ul>{check.conflicts.map(conflict => <li key={conflict.entryId}>
      <strong>{listLabels[conflict.list] || conflict.list}</strong> · {conflict.players.map(playerName).join(" & ")}: {conflict.reason}
    </li>)}</ul>
    <Link to={entriesLink}>Open Entries</Link>
  </div>
}
